import { create } from 'zustand';
import { en } from './en.js';
import { pt } from './pt.js';

/**
 * Textos da interface. São todos escritos para o RPG Desk; as chaves seguem as áreas
 * de tela do spec (docs/spec/screens) para facilitar conferir cobertura.
 */
export type Dict = Record<string, string>;
export type Language = 'pt-BR' | 'en';

export const LANGUAGES: readonly { id: Language; label: string }[] = [
  { id: 'pt-BR', label: 'Português (Brasil)' },
  { id: 'en', label: 'English' },
];

const DICTS: Record<Language, Dict> = { 'pt-BR': pt, en };
const LANG_KEY = 'rpgdesk.language';

function initialLanguage(): Language {
  try {
    const saved = window.localStorage.getItem(LANG_KEY);
    if (saved === 'pt-BR' || saved === 'en') return saved;
  } catch {
    // sem armazenamento: cai para o idioma do sistema
  }
  return typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('pt') ? 'pt-BR' : 'en';
}

export const useLanguage = create<{ language: Language; setLanguage: (l: Language) => void }>((set) => ({
  language: typeof window === 'undefined' ? 'pt-BR' : initialLanguage(),
  setLanguage(language) {
    try {
      window.localStorage.setItem(LANG_KEY, language);
    } catch {
      // ignora: a escolha vale só nesta sessão
    }
    document.documentElement.lang = language;
    set({ language });
  },
}));

// datas e números seguem o idioma escolhido (toLocale* usa o lang da página)
if (typeof document !== 'undefined') document.documentElement.lang = useLanguage.getState().language;

/** Traduz `key`, trocando `{nome}` pelos parâmetros. Sem tradução, mostra a chave. */
export function translate(language: Language, key: string, params?: Record<string, string | number>): string {
  const text = DICTS[language][key] ?? DICTS['pt-BR'][key] ?? key;
  return params ? text.replace(/\{(\w+)\}/g, (_, k: string) => String(params[k] ?? `{${k}}`)) : text;
}

export function useT(): (key: string, params?: Record<string, string | number>) => string {
  const language = useLanguage((s) => s.language);
  return (key, params) => translate(language, key, params);
}
